import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { VersionWatch } from "@/components/VersionWatch";
import { ConfirmProvider } from "@/components/ConfirmDialog";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Desk from "@/pages/Desk";
import AdminDesk from "@/pages/AdminDesk";
import MeetingManage from "@/pages/MeetingManage";
import Pipeline from "@/pages/Pipeline";
import Ads from "@/pages/Ads";
import Funnel from "@/pages/Funnel";
import Settings from "@/pages/Settings";
import Skills from "@/pages/Skills";
import Forms from "@/pages/Forms";
import FormEditor from "@/pages/FormEditor";
import PublicForm from "@/pages/PublicForm";
import BookingPage from "@/pages/BookingPage";
import CRM from "@/pages/CRM";
import CrmSetup from "@/pages/CrmSetup";
import ClaudeRuns from "@/pages/ClaudeRuns";
import ClaudePage from "@/pages/ClaudePage";
import TeamActivity from "@/pages/TeamActivity";
import CalendarPage from "@/pages/CalendarPage";
import TasksPage from "@/pages/TasksPage";
import ModulePage from "@/pages/ModulePage";
import OAuthConsent from "@/pages/OAuthConsent";
import { isFormHost } from "@/lib/api";

function Protected({ children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen bg-black text-neutral-500 flex items-center justify-center text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace state={{ from: loc }} />;
  return children;
}

const APP_PATHS = ["login", "oauth", "crm", "forms", "claude", "team", "calendar", "tasks", "modules", "desk", "pipeline", "ads", "funnel", "settings", "skills", "admin-desk"];

function Away({ to }) {
  window.location.replace(to);
  return <div className="min-h-screen bg-black" data-testid="host-redirect" />;
}

function BookingRoute() {
  const slug = useLocation().pathname.split("/")[2];
  return <BookingPage slug={slug} />;
}

function HostSlug() {
  const loc = useLocation();
  const first = loc.pathname.split("/")[1];
  if (APP_PATHS.includes(first)) return <Away to={`https://sales.thickandthin.media${loc.pathname}${loc.search}`} />;
  return <PublicForm />;
}

export default function App() {
  if (isFormHost()) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Away to="https://www.thickandthin.media" />} />
          <Route path="/:slug" element={<HostSlug />} />
          <Route path="/f/:slug" element={<PublicForm />} />
          <Route path="/b/:slug" element={<BookingRoute />} />
          <Route path="/m/:token" element={<MeetingManage />} />
          <Route path="*" element={<div className="min-h-screen bg-black text-neutral-500 flex items-center justify-center text-sm" data-testid="host-404">Not found</div>} />
        </Routes>
        <Toaster theme="dark" position="top-right" />
      </BrowserRouter>
    );
  }
  return (
    <AuthProvider>
      <ConfirmProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/oauth/consent" element={<OAuthConsent />} />
          <Route path="/f/:slug" element={<PublicForm />} />
          <Route path="/b/:slug" element={<BookingRoute />} />
          <Route path="/m/:token" element={<MeetingManage />} />
          <Route element={<Protected><Layout /></Protected>}>
            <Route index element={<Navigate to="/crm" replace />} />
            <Route path="/crm" element={<CRM />} />
            <Route path="/crm/setup" element={<CrmSetup />} />
            <Route path="/forms" element={<Forms />} />
            <Route path="/forms/:id" element={<FormEditor />} />
            <Route path="/claude" element={<ClaudePage />} />
            <Route path="/team" element={<TeamActivity />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/modules/:key" element={<ModulePage />} />
            <Route path="/desk" element={<Desk />} />
            <Route path="/admin-desk" element={<AdminDesk />} />
            <Route path="/pipeline" element={<Pipeline />} />
            <Route path="/ads" element={<Ads />} />
            <Route path="/funnel" element={<Funnel />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/skills" element={<Skills />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster theme="dark" position="top-right" />
      <VersionWatch />
      </ConfirmProvider>
    </AuthProvider>
  );
}
